import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatEUR, firstCategoryWithProducts } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Plus, Minus, Trash2, Send, FileText, Users, ChevronRight, Search, X, Coffee, UtensilsCrossed } from "lucide-react";
import OperatorSwitcher from "@/components/OperatorSwitcher";
import CheckUpdateButton from "@/components/CheckUpdateButton";
import PinLockScreen from "@/components/PinLockScreen";
import { tableStatusOf } from "@/lib/tableStatus";
import { COURSE_OPTIONS, groupLinesByCourse, stationLabel } from "@/lib/comanda";
import { useAuth } from "@/lib/auth";
import usePrecontoPrinter from "@/lib/usePrecontoPrinter";
import { printComandeViaQz, printReceiptViaQz, thermalPrintError } from "@/lib/printService";

/**
 * Palmare comande — UI mobile-first per cameriere
 * Flusso: lista tavoli → tavolo → categorie → prodotti → carrello → invio cucina/bar (no fiscale)
 */
export default function Palmare() {
  return (
    <PinLockScreen>
      <PalmareApp />
    </PinLockScreen>
  );
}

function PalmareApp() {
  const { user } = useAuth() || {};
  const [view, setView] = useState("tables"); // tables | order | products
  const [tables, setTables] = useState([]);
  const [activeRoom, setActiveRoom] = useState(null);
  const [cats, setCats] = useState([]);
  const [prods, setProds] = useState([]);
  const [activeCat, setActiveCat] = useState(null);
  const [activeTable, setActiveTable] = useState(null);
  const [order, setOrder] = useState(null);
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);
  const [activeCourse, setActiveCourse] = useState("");
  const { hasPreconto } = usePrecontoPrinter();

  const loadTables = () => api.get("/tables").then(r => {
    setTables(Array.isArray(r.data) ? r.data : []);
    const rooms = [...new Set((Array.isArray(r.data) ? r.data : []).map(t => t.room))];
    if (rooms.length && !activeRoom) setActiveRoom(rooms[0]);
  });

  useEffect(() => {
    loadTables();
    api.get("/categories").then(r => setCats((Array.isArray(r.data) ? r.data : []).filter((c) => c.is_available_on_handheld !== false)));
    api.get("/products").then(r => setProds((Array.isArray(r.data) ? r.data : []).filter((p) => p.effective_handheld !== false && p.is_available_on_handheld !== false)));
    const t = setInterval(loadTables, 8000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (activeCat == null && cats.length && prods.length) setActiveCat(firstCategoryWithProducts(cats, prods));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cats, prods]);

  const rooms = useMemo(() => [...new Set((Array.isArray(tables) ? tables : []).map(t => t.room))], [tables]);
  const tablesInRoom = useMemo(() => (Array.isArray(tables) ? tables : []).filter(t => t.room === activeRoom && !t.joined_parent_id), [tables, activeRoom]);

  const openTable = async (t) => {
    setActiveTable(t);
    if (t.open_order_id) {
      const r = await api.get(`/orders/${t.open_order_id}`);
      setOrder(r.data);
    } else {
      setOrder({ lines: [], subtotal: 0, total: 0, table_id: t.id, mode: "ristorante" });
    }
    setView("order");
  };

  const recomp = (lines) => {
    let s = 0;
    for (const l of lines) s += l.price * l.qty;
    setOrder({ ...order, lines, subtotal: s, total: s });
  };

  const addProd = (p) => {
    const lines = [...(order?.lines || [])];
    const i = lines.findIndex(l => l.product_id === p.id && !l.note && (l.course || "") === (activeCourse || ""));
    if (i >= 0) lines[i] = { ...lines[i], qty: lines[i].qty + 1 };
    else lines.push({
      product_id: p.id, name: p.name, qty: 1, price: p.price, vat: p.vat,
      print_to: p.effective_print_to || p.print_to,
      print_station: p.effective_print_station || p.print_station || p.effective_print_to || p.print_to,
      category_id: p.category_id,
      course: activeCourse || null,
    });
    recomp(lines);
    toast.success(`+1 ${p.name}`, { duration: 1200 });
  };

  const inc = (i, d) => {
    const lines = [...order.lines];
    lines[i] = { ...lines[i], qty: Math.max(0, lines[i].qty + d) };
    if (lines[i].qty === 0) lines.splice(i, 1);
    recomp(lines);
  };

  const del = (i) => {
    const lines = [...order.lines]; lines.splice(i, 1); recomp(lines);
  };

  const sendToKitchen = async () => {
    if (!order?.lines?.length) return toast.error("Aggiungi articoli");
    setBusy(true);
    try {
      const r = await api.post("/orders", {
        mode: "ristorante",
        table_id: order.table_id,
        notes: order.notes || null,
        lines: (Array.isArray(order.lines) ? order.lines : []).map(l => ({ ...l })),
      });
      const grouped = (r.data.lines || []).reduce((a, l) => { a[l.print_to || "_"] = (a[l.print_to || "_"] || 0) + 1; return a; }, {});
      const where = Object.keys(grouped).filter(k => k !== "_").map(k => k === "kitchen" ? "🍳 cucina" : "🍹 bar").join(" + ") || "registrato";
      toast.success(`Inviato → ${where}`);
      printComandeViaQz({
        order: r.data,
        tableName: activeTable?.name,
        roomName: activeTable?.room,
        operator: r.data.last_operator_name || r.data.operator_name || user?.name,
      })
        .then((res) => {
          if (res.ok) toast.success(`Stampato: ${(Array.isArray(res.printed) ? res.printed : []).map((p) => p.printer).join(", ")}`);
          else toast.error(thermalPrintError(res), { duration: 8000 });
        })
        .catch((e) => toast.error(String(e?.message || e)));
      setOrder(null); setActiveTable(null); setView("tables");
      loadTables();
    } catch { toast.error("Errore invio"); }
    finally { setBusy(false); }
  };

  const preconto = async () => {
    if (!order?.lines?.length) return toast.error("Conto vuoto");
    setBusy(true);
    try {
      let oid = order.id;
      if (!oid) {
        const r = await api.post("/orders", {
          mode: "ristorante",
          table_id: order.table_id,
          lines: (Array.isArray(order.lines) ? order.lines : []).map(l => ({ ...l })),
        });
        oid = r.data.id;
      }
      const pr = await api.post(`/orders/${oid}/preconto`);
      const res = await printReceiptViaQz({
        tenant: {},
        order: { ...(pr.data?.order || {}), receipt_type: "preconto" },
        kind: "preconto",
      });
      if (res.ok) toast.success(`Preconto su "${res.printer}"`);
      else toast.error(thermalPrintError(res), { duration: 8000 });
      setOrder(null); setActiveTable(null); setView("tables");
      loadTables();
    } catch { toast.error("Errore"); }
    finally { setBusy(false); }
  };

  const filteredProds = useMemo(() => {
    const base = activeCat ? (Array.isArray(prods) ? prods : []).filter(p => p.category_id === activeCat) : prods;
    if (!query) return (Array.isArray(base) ? base : []).filter(p => p.visible !== false);
    return (Array.isArray(prods) ? prods : []).filter(p => p.visible !== false && p.name.toLowerCase().includes(query.toLowerCase()));
  }, [prods, activeCat, query]);

  // ============ VIEW: TABLES ============
  if (view === "tables") {
    return (
      <div className="h-screen bg-velta-bg flex flex-col overflow-hidden max-w-md mx-auto">
        <header className="h-14 border-b border-velta-border bg-white flex items-center px-4 gap-3 shrink-0">
          <Link to="/dashboard" data-testid="palm-exit" className="text-velta-mute hover:text-velta-ink"><ArrowLeft className="w-5 h-5" /></Link>
          <div className="font-heading font-semibold tracking-tight flex-1">Palmare · Comande</div>
          <OperatorSwitcher compact />
          <button data-testid="palm-refresh" onClick={loadTables} className="text-xs text-velta-mute font-medium">Aggiorna</button>
        </header>

        {/* Room tabs */}
        <div className="border-b border-velta-border bg-white px-3 py-2 flex gap-2 overflow-x-auto pos-scroll">
          {(Array.isArray(rooms) ? rooms : []).map(r => (
            <button
              key={r}
              data-testid={`palm-room-${r}`}
              onClick={() => setActiveRoom(r)}
              className={`shrink-0 h-10 px-4 rounded-full text-sm font-medium transition ${activeRoom === r ? "bg-velta-ink text-white" : "bg-velta-bg text-velta-ink border border-velta-border"}`}
            >
              {r}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-auto pos-scroll p-3">
          <div className="grid grid-cols-3 gap-2">
            {(Array.isArray(tablesInRoom) ? tablesInRoom : []).map(t => (
              <button
                key={t.id}
                data-testid={`palm-table-${t.id}`}
                onClick={() => openTable(t)}
                className={`aspect-square rounded-md border-2 flex flex-col items-center justify-center btn-tactile transition ${tableStatusOf(t).className}`}
              >
                <div className="font-mono font-bold text-2xl tracking-tight">{t.name}</div>
                <div className="text-[10px] text-velta-mute flex items-center gap-1">
                  <Users className="w-3 h-3" /> {t.seats}
                </div>
                {t.open_order_id && (
                  <div className="text-xs font-mono font-bold text-[#2F9E77] mt-1">{formatEUR(t.open_total)}</div>
                )}
              </button>
            ))}
          </div>
          {tablesInRoom.length === 0 && <div className="text-center text-velta-mute text-sm py-12">Nessun tavolo in {activeRoom}.</div>}
        </div>

        <div className="border-t border-velta-border bg-white px-2 py-1">
          <CheckUpdateButton variant="sidebar" auto />
        </div>
        {/* Stats footer */}
        <div className="border-t border-velta-border bg-white px-4 py-3 flex items-center justify-between text-xs">
          <div className="text-velta-mute">Tavoli occupati</div>
          <div className="font-mono font-bold">
            <span data-testid="palm-occupied">{(Array.isArray(tables) ? tables : []).filter(t => t.open_order_id).length}</span>
            <span className="text-velta-mute"> / {tables.length}</span>
          </div>
        </div>
      </div>
    );
  }

  // ============ VIEW: ORDER (cart) ============
  if (view === "order") {
    const linesCount = order?.lines?.length || 0;
    return (
      <div className="h-screen bg-velta-bg flex flex-col overflow-hidden max-w-md mx-auto">
        <header className="h-14 border-b border-velta-border bg-white flex items-center px-3 gap-3 shrink-0">
          <button data-testid="palm-back-tables" onClick={() => { setOrder(null); setActiveTable(null); setView("tables"); }} className="text-velta-mute"><ArrowLeft className="w-5 h-5" /></button>
          <div className="flex-1">
            <div className="font-heading font-bold text-lg leading-none">Tavolo {activeTable?.name}</div>
            <div className="text-xs text-velta-mute">{activeTable?.room} · {activeTable?.seats} posti</div>
          </div>
          <button data-testid="palm-add-items" onClick={() => setView("products")} className="h-10 px-4 bg-velta-ink text-white rounded-md text-sm font-medium btn-tactile inline-flex items-center gap-1">
            <Plus className="w-4 h-4" /> Aggiungi
          </button>
        </header>

        <div className="flex-1 overflow-auto pos-scroll p-3">
          {linesCount === 0 ? (
            <div className="text-center text-velta-mute text-sm py-16">
              <UtensilsCrossed className="w-12 h-12 mx-auto mb-3 opacity-40" />
              Conto vuoto. Tocca <span className="font-semibold">Aggiungi</span> per iniziare.
            </div>
          ) : (
            <div className="space-y-3">
              {groupLinesByCourse(order.lines).map((g) => (
                <div key={g.key}>
                  {g.label && <div className="text-[10px] font-mono uppercase tracking-widest text-velta-mute mb-1 px-1">{g.label}</div>}
                  <div className="space-y-2">
                    {g.items.map(({ line: l, index: i }) => (
                <div key={i} data-testid={`palm-line-${i}`} className="bg-white border border-velta-border rounded-md p-3 flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate">{l.name}</div>
                    <div className="text-xs text-velta-mute">{formatEUR(l.price)} · {stationLabel(l.print_station || l.print_to)}</div>
                    <input
                      data-testid={`palm-line-note-${i}`}
                      value={l.note || ""}
                      onChange={(e) => {
                        const lines = [...order.lines];
                        lines[i] = { ...lines[i], note: e.target.value };
                        recomp(lines);
                      }}
                      placeholder="Nota riga (cucina)…"
                      className="mt-1 w-full h-8 px-2 text-xs border border-velta-border rounded"
                    />
                  </div>
                  <div className="inline-flex items-center border border-velta-border rounded-md">
                    <button data-testid={`palm-line-minus-${i}`} onClick={() => inc(i, -1)} className="w-8 h-8 flex items-center justify-center"><Minus className="w-3.5 h-3.5" /></button>
                    <span className="w-7 text-center font-mono font-bold">{l.qty}</span>
                    <button data-testid={`palm-line-plus-${i}`} onClick={() => inc(i, 1)} className="w-8 h-8 flex items-center justify-center"><Plus className="w-3.5 h-3.5" /></button>
                  </div>
                  <button onClick={() => del(i)} className="text-velta-error"><Trash2 className="w-4 h-4" /></button>
                </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="border-t border-velta-border bg-white p-3 space-y-2">
          <div className="flex gap-1 overflow-x-auto pos-scroll pb-1" data-testid="palm-course-bar">
            {COURSE_OPTIONS.map((c) => (
              <button
                key={c.id || "none"}
                type="button"
                onClick={() => setActiveCourse(c.id)}
                className={`shrink-0 h-8 px-3 rounded-full text-[11px] font-medium ${activeCourse === c.id ? "bg-velta-ink text-white" : "bg-velta-bg border border-velta-border"}`}
              >
                {c.label}
              </button>
            ))}
          </div>
          <input
            data-testid="palm-kitchen-note"
            value={order?.notes || ""}
            onChange={(e) => setOrder({ ...order, notes: e.target.value })}
            placeholder="Note per la cucina…"
            className="w-full h-11 px-3 border border-velta-border rounded-md text-sm"
          />
          <div className="flex items-center justify-between px-1">
            <span className="text-xs font-mono uppercase tracking-widest text-velta-mute">Totale</span>
            <span data-testid="palm-total" className="font-mono font-bold text-3xl tracking-tighter">{formatEUR(order?.total || 0)}</span>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {hasPreconto && (
              <button
                data-testid="palm-preconto"
                disabled={!linesCount || busy}
                onClick={preconto}
                className="h-14 bg-white border-2 border-velta-ink text-velta-ink rounded-md font-medium btn-tactile disabled:opacity-40 flex items-center justify-center gap-2"
              >
                <FileText className="w-4 h-4" /> Preconto
              </button>
            )}
            <button
              data-testid="palm-send"
              disabled={!linesCount || busy}
              onClick={sendToKitchen}
              className={`h-14 bg-[#2F9E77] text-white rounded-md font-medium btn-tactile disabled:opacity-40 flex items-center justify-center gap-2 ${hasPreconto ? "" : "col-span-2"}`}
            >
              <Send className="w-4 h-4" /> Invia
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ============ VIEW: PRODUCTS ============
  return (
    <div className="h-screen bg-velta-bg flex flex-col overflow-hidden max-w-md mx-auto">
      <header className="h-14 border-b border-velta-border bg-white flex items-center px-3 gap-3 shrink-0">
        <button data-testid="palm-back-order" onClick={() => setView("order")} className="text-velta-mute"><ArrowLeft className="w-5 h-5" /></button>
        <div className="font-heading font-semibold text-base flex-1">Tav. {activeTable?.name} → Articoli</div>
        <button data-testid="palm-close-products" onClick={() => setView("order")} className="text-velta-mute"><X className="w-5 h-5" /></button>
      </header>
      <div className="px-3 py-2 bg-white border-b border-velta-border flex gap-1 overflow-x-auto pos-scroll">
        {COURSE_OPTIONS.map((c) => (
          <button key={c.id || "none"} type="button" onClick={() => setActiveCourse(c.id)} className={`shrink-0 h-8 px-3 rounded-full text-[11px] font-medium ${activeCourse === c.id ? "bg-velta-ink text-white" : "bg-velta-bg border border-velta-border"}`}>
            {c.label}
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="px-3 py-2 bg-white border-b border-velta-border">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-3 text-velta-mute" />
          <input
            data-testid="palm-search"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Cerca articolo…"
            className="w-full h-10 pl-9 pr-3 border border-velta-border rounded-md text-sm"
          />
        </div>
      </div>

      {/* Cat tabs */}
      {!query && (
        <div className="bg-white border-b border-velta-border px-3 py-2 flex gap-2 overflow-x-auto pos-scroll">
          {(Array.isArray(cats) ? cats : []).map(c => (
            <button
              key={c.id}
              data-testid={`palm-cat-${c.id}`}
              onClick={() => setActiveCat(c.id)}
              className={`shrink-0 h-10 px-4 rounded-full text-sm font-medium ${activeCat === c.id ? "text-white" : "bg-velta-bg border border-velta-border text-velta-ink"}`}
              style={activeCat === c.id ? { background: c.color } : {}}
            >
              {c.name}
            </button>
          ))}
        </div>
      )}

      <div className="flex-1 overflow-auto pos-scroll p-3">
        <div className="grid grid-cols-2 gap-2">
          {(Array.isArray(filteredProds) ? filteredProds : []).map(p => {
            const cat = cats.find(c => c.id === p.category_id);
            const color = p.color || cat?.color || "#0A0A0A";
            return (
              <button
                key={p.id}
                data-testid={`palm-prod-${p.id}`}
                onClick={() => addProd(p)}
                className="relative bg-white border border-velta-border rounded-md p-3 h-24 text-left flex flex-col justify-between hover:border-velta-ink btn-tactile"
              >
                <div className="absolute top-0 left-0 right-0 h-1" style={{ background: color }} />
                <div className="font-medium text-xs leading-tight mt-1">{p.name}</div>
                {p.show_price !== false && <div className="font-mono font-bold text-sm">{formatEUR(p.price)}</div>}
                {p.print_to && <div className="absolute top-2 right-2 text-[9px]">{p.print_to === "kitchen" ? "🍳" : "🍹"}</div>}
              </button>
            );
          })}
        </div>
        {filteredProds.length === 0 && <div className="text-center text-velta-mute text-sm py-8">Nessun risultato.</div>}
      </div>

      {/* Sticky cart hint */}
      {order?.lines?.length > 0 && (
        <div className="border-t border-velta-border bg-white px-3 py-2 flex items-center justify-between">
          <div className="text-xs">
            <span className="font-mono font-bold">{order.lines.reduce((s, l) => s + l.qty, 0)}</span> articoli · <span className="font-mono font-bold">{formatEUR(order.total)}</span>
          </div>
          <button data-testid="palm-go-cart" onClick={() => setView("order")} className="h-10 px-4 bg-velta-ink text-white rounded-md text-sm font-medium inline-flex items-center gap-1 btn-tactile">
            Vedi conto <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
}
